import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isValidString } from '../../common/CommonUtilsMethods';

dayjs.extend(utc);
dayjs.extend(timezone);

const EASTERN_TIME_ZONE: string = 'America/New_York';
const DATE_INPUT_MIN_LENGTH: number = 10;

const BILLING_DAY_NAME_TO_DAYJS_INDEX: Readonly<Record<string, number>> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const dateInputValueFromEpochSecEastern = (epochSec: number): string => {
  if (!epochSec || epochSec <= 0) {
    return '';
  }
  return dayjs.unix(epochSec).tz(EASTERN_TIME_ZONE).format('YYYY-MM-DD');
};

const epochSecFromDateInputEasternStartOfDay = (yyyyMmDd: string): number | null => {
  if (!isValidString(yyyyMmDd) || yyyyMmDd.length < DATE_INPUT_MIN_LENGTH) {
    return null;
  }
  const parsed = dayjs.tz(yyyyMmDd, 'YYYY-MM-DD', EASTERN_TIME_ZONE);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.startOf('day').unix();
};

const DAYJS_INDEX_TO_BILLING_DAY_NAME: Readonly<Record<number, string>> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

const deriveBillingDayNameFromAnchorDateEastern = (yyyyMmDd: string): string | null => {
  if (!isValidString(yyyyMmDd) || yyyyMmDd.length < DATE_INPUT_MIN_LENGTH) {
    return null;
  }
  const parsed = dayjs.tz(yyyyMmDd, 'YYYY-MM-DD', EASTERN_TIME_ZONE);
  if (!parsed.isValid()) {
    return null;
  }
  return DAYJS_INDEX_TO_BILLING_DAY_NAME[parsed.day()] ?? null;
};

const doesDateInputMatchBillingDayEastern = (yyyyMmDd: string, billingDay: string): boolean => {
  if (!isValidString(yyyyMmDd) || !isValidString(billingDay)) {
    return false;
  }
  const parsed = dayjs.tz(yyyyMmDd, 'YYYY-MM-DD', EASTERN_TIME_ZONE);
  if (!parsed.isValid()) {
    return false;
  }
  const expectedDayIndex: number | undefined = BILLING_DAY_NAME_TO_DAYJS_INDEX[billingDay.toUpperCase()];
  if (expectedDayIndex === undefined) {
    return false;
  }
  return parsed.day() === expectedDayIndex;
};

const formatDateOnlyFromEpochEastern = (epochSec: number): string => {
  if (!epochSec || epochSec <= 0) {
    return '—';
  }
  return `${dayjs.unix(epochSec).tz(EASTERN_TIME_ZONE).format('MMM D, YYYY')} (Eastern Time)`;
};

export {
  dateInputValueFromEpochSecEastern,
  deriveBillingDayNameFromAnchorDateEastern,
  doesDateInputMatchBillingDayEastern,
  epochSecFromDateInputEasternStartOfDay,
  formatDateOnlyFromEpochEastern,
};
